import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";

// Редактор стрелок-указателей на фото перед отправкой. Нужен саппорту, чтобы
// вести клиента по скриншоту («нажми вот сюда»).
//
// Всё рисование идёт в НАТУРАЛЬНЫХ координатах картинки: canvas имеет
// width/height оригинала, а на экран его ужимает CSS. Поэтому то, что видит
// саппорт, попиксельно совпадает с тем, что уйдёт в чат, и не нужен второй
// «экспортный» холст со своим масштабированием.
//
// Зум: CSS-размер холста = «вписанный в сцену» размер × zoom. Сцена
// (viewport) со скроллом — при увеличении картинка листается колёсиком /
// скроллбарами, тащится средней кнопкой, зажатым пробелом или в режиме
// «Рука». Ctrl/⌘ + колесо и «+»/«−» приближают к курсору, как в Telegram.

interface ArrowShape {
  // Доли от ширины/высоты картинки (0..1) — не зависят от размера окна.
  fromX: number;
  fromY: number;
  toX: number;
  toY: number;
  color: string;
  // Толщина линии как доля диагонали картинки: одна и та же стрелка
  // одинаково смотрится и на скриншоте 800px, и на фото 4000px.
  widthRatio: number;
}

const COLORS = [
  { value: "#ef4444", label: "Красный" },
  { value: "#f59e0b", label: "Оранжевый" },
  { value: "#22c55e", label: "Зелёный" },
  { value: "#2563eb", label: "Синий" },
  { value: "#ffffff", label: "Белый" },
  { value: "#111827", label: "Чёрный" }
];

const THICKNESS = [
  { value: 0.004, label: "Тонкая" },
  { value: 0.007, label: "Средняя" },
  { value: 0.012, label: "Толстая" }
];

// Минимальная длина стрелки в долях диагонали: случайный клик без протяжки
// не должен оставлять «кляксу» из одной головки.
const MIN_ARROW_RATIO = 0.02;

const ZOOM_MIN = 1;
const ZOOM_MAX = 8;
const ZOOM_STEP = 1.25;
const clampZoom = (value: number): number =>
  Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, Number.isFinite(value) ? value : ZOOM_MIN));

const drawArrow = (
  ctx: CanvasRenderingContext2D,
  arrow: ArrowShape,
  imageWidth: number,
  imageHeight: number
): void => {
  const diagonal = Math.hypot(imageWidth, imageHeight);
  const lineWidth = Math.max(2, diagonal * arrow.widthRatio);
  const fromX = arrow.fromX * imageWidth;
  const fromY = arrow.fromY * imageHeight;
  const toX = arrow.toX * imageWidth;
  const toY = arrow.toY * imageHeight;
  const angle = Math.atan2(toY - fromY, toX - fromX);
  const headLength = lineWidth * 3.2;
  // Линию не доводим до самого острия — иначе она вылезает за головку и
  // кончик выглядит тупым.
  const shaftX = toX - Math.cos(angle) * headLength * 0.8;
  const shaftY = toY - Math.sin(angle) * headLength * 0.8;

  ctx.save();
  // Тёмная обводка под стрелкой: на пёстром скриншоте цветная линия иначе
  // теряется (белая на светлом фоне — вообще не видна).
  ctx.lineCap = "round";
  ctx.lineJoin = "round";
  ctx.strokeStyle = "rgba(0, 0, 0, 0.35)";
  ctx.lineWidth = lineWidth * 1.9;
  ctx.beginPath();
  ctx.moveTo(fromX, fromY);
  ctx.lineTo(shaftX, shaftY);
  ctx.stroke();

  ctx.strokeStyle = arrow.color;
  ctx.fillStyle = arrow.color;
  ctx.lineWidth = lineWidth;
  ctx.beginPath();
  ctx.moveTo(fromX, fromY);
  ctx.lineTo(shaftX, shaftY);
  ctx.stroke();

  ctx.beginPath();
  ctx.moveTo(toX, toY);
  ctx.lineTo(
    toX - Math.cos(angle - Math.PI / 7) * headLength,
    toY - Math.sin(angle - Math.PI / 7) * headLength
  );
  ctx.lineTo(
    toX - Math.cos(angle + Math.PI / 7) * headLength,
    toY - Math.sin(angle + Math.PI / 7) * headLength
  );
  ctx.closePath();
  ctx.strokeStyle = "rgba(0, 0, 0, 0.35)";
  ctx.lineWidth = lineWidth * 0.9;
  ctx.stroke();
  ctx.fill();
  ctx.restore();
};

interface PhotoAnnotatorProps {
  file: File;
  onCancel(): void;
  // Отдаёт новый файл с врисованными стрелками (имя сохраняем, расширение
  // приводим к формату экспорта).
  onApply(file: File): void;
}

type Tool = "arrow" | "pan";

export const PhotoAnnotator = ({ file, onCancel, onApply }: PhotoAnnotatorProps) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const viewportRef = useRef<HTMLDivElement | null>(null);
  const imageRef = useRef<HTMLImageElement | null>(null);
  const [isReady, setIsReady] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [arrows, setArrows] = useState<ArrowShape[]>([]);
  const [color, setColor] = useState(COLORS[0]!.value);
  const [widthRatio, setWidthRatio] = useState(THICKNESS[1]!.value);
  const [tool, setTool] = useState<Tool>("arrow");
  const [zoom, setZoom] = useState(1);
  // Размер холста «по размеру сцены» (zoom = 1): пересчитывается при
  // ресайзе окна через ResizeObserver.
  const [fitSize, setFitSize] = useState<{ width: number; height: number } | null>(null);
  // Точка, вокруг которой применяется следующий зум (координаты внутри
  // viewport'а, px). null = центр видимой области.
  const zoomAnchorRef = useRef<{ x: number; y: number } | null>(null);
  const previousZoomRef = useRef(1);
  // Стрелка «в процессе перетаскивания»: живём в ref, чтобы каждое движение
  // мыши не гоняло React-рендер большого дерева чата.
  const draftRef = useRef<ArrowShape | null>(null);
  // Панорамирование: стартовая позиция курсора и скролла.
  const panRef = useRef<{
    pointerId: number;
    startX: number;
    startY: number;
    scrollLeft: number;
    scrollTop: number;
  } | null>(null);
  const [isPanning, setIsPanning] = useState(false);
  const [spaceHeld, setSpaceHeld] = useState(false);

  useEffect(() => {
    const url = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => {
      imageRef.current = image;
      setIsReady(true);
    };
    image.onerror = () => setLoadFailed(true);
    image.src = url;
    return () => {
      URL.revokeObjectURL(url);
      imageRef.current = null;
    };
  }, [file]);

  const redraw = useCallback(() => {
    const canvas = canvasRef.current;
    const image = imageRef.current;
    if (!canvas || !image) {
      return;
    }
    const ctx = canvas.getContext("2d");
    if (!ctx) {
      return;
    }
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(image, 0, 0, canvas.width, canvas.height);
    for (const arrow of arrows) {
      drawArrow(ctx, arrow, canvas.width, canvas.height);
    }
    if (draftRef.current) {
      drawArrow(ctx, draftRef.current, canvas.width, canvas.height);
    }
  }, [arrows]);

  useEffect(() => {
    const canvas = canvasRef.current;
    const image = imageRef.current;
    if (!isReady || !canvas || !image) {
      return;
    }
    canvas.width = image.naturalWidth;
    canvas.height = image.naturalHeight;
    redraw();
  }, [isReady, redraw]);

  // «Вписанный» размер: картинка целиком помещается в сцену с сохранением
  // пропорций, но не растягивается больше натурального размера.
  useLayoutEffect(() => {
    const viewport = viewportRef.current;
    const image = imageRef.current;
    if (!isReady || !viewport || !image) {
      return;
    }
    const compute = (): void => {
      const availableWidth = Math.max(1, viewport.clientWidth - 16);
      const availableHeight = Math.max(1, viewport.clientHeight - 16);
      const naturalWidth = Math.max(1, image.naturalWidth);
      const naturalHeight = Math.max(1, image.naturalHeight);
      const scale = Math.min(
        1,
        availableWidth / naturalWidth,
        availableHeight / naturalHeight
      );
      const next = {
        width: Math.max(1, Math.round(naturalWidth * scale)),
        height: Math.max(1, Math.round(naturalHeight * scale))
      };
      setFitSize((previous) =>
        previous && previous.width === next.width && previous.height === next.height
          ? previous
          : next
      );
    };
    compute();
    if (typeof ResizeObserver === "undefined") {
      return;
    }
    const observer = new ResizeObserver(() => compute());
    observer.observe(viewport);
    return () => observer.disconnect();
  }, [isReady]);

  // Держим точку под курсором на месте при смене зума: скролл сдвигаем
  // пропорционально. Выполняется после того, как новый CSS-размер холста
  // применён (layout effect по zoom).
  useLayoutEffect(() => {
    const viewport = viewportRef.current;
    const previousZoom = previousZoomRef.current;
    previousZoomRef.current = zoom;
    if (!viewport || previousZoom === zoom) {
      return;
    }
    const anchor = zoomAnchorRef.current ?? {
      x: viewport.clientWidth / 2,
      y: viewport.clientHeight / 2
    };
    zoomAnchorRef.current = null;
    const ratio = zoom / previousZoom;
    viewport.scrollLeft = (viewport.scrollLeft + anchor.x) * ratio - anchor.x;
    viewport.scrollTop = (viewport.scrollTop + anchor.y) * ratio - anchor.y;
  }, [zoom]);

  const applyZoom = useCallback(
    (next: number, anchor?: { x: number; y: number } | null) => {
      const clamped = clampZoom(next);
      zoomAnchorRef.current = anchor ?? null;
      setZoom((previous) => (Math.abs(previous - clamped) < 0.001 ? previous : clamped));
    },
    []
  );

  // Ctrl/⌘ + колесо — зум к курсору. Обычное колесо оставляем нативному
  // скроллу сцены (панорамирование). Слушатель нативный и non-passive:
  // иначе браузер увеличит всю страницу.
  useEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport || !isReady) {
      return;
    }
    const onWheel = (event: WheelEvent): void => {
      if (!(event.ctrlKey || event.metaKey)) {
        return;
      }
      event.preventDefault();
      const rect = viewport.getBoundingClientRect();
      const anchor = { x: event.clientX - rect.left, y: event.clientY - rect.top };
      // Тачпад шлёт мелкие дельты, мышь — крупные; нормализуем в плавный шаг.
      const factor = Math.exp(-event.deltaY * 0.0025);
      setZoom((previous) => {
        const next = clampZoom(previous * factor);
        zoomAnchorRef.current = anchor;
        return next;
      });
    };
    viewport.addEventListener("wheel", onWheel, { passive: false });
    return () => viewport.removeEventListener("wheel", onWheel);
  }, [isReady]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === "Escape") {
        onCancel();
        return;
      }
      const target = event.target as HTMLElement | null;
      if (target && (target.tagName === "INPUT" || target.tagName === "TEXTAREA")) {
        return;
      }
      if (event.code === "Space") {
        // Зажатый пробел — временный режим «Рука», как в графических
        // редакторах.
        event.preventDefault();
        setSpaceHeld(true);
        return;
      }
      if (event.key === "+" || event.key === "=") {
        event.preventDefault();
        applyZoom(clampZoom(previousZoomRef.current * ZOOM_STEP));
        return;
      }
      if (event.key === "-" || event.key === "_") {
        event.preventDefault();
        applyZoom(clampZoom(previousZoomRef.current / ZOOM_STEP));
        return;
      }
      if (event.key === "0") {
        event.preventDefault();
        applyZoom(1);
      }
    };
    const onKeyUp = (event: KeyboardEvent): void => {
      if (event.code === "Space") {
        setSpaceHeld(false);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("keyup", onKeyUp);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("keyup", onKeyUp);
    };
  }, [applyZoom, onCancel]);

  const pointFromEvent = (
    event: React.PointerEvent<HTMLCanvasElement>
  ): { x: number; y: number } => {
    const rect = event.currentTarget.getBoundingClientRect();
    return {
      x: rect.width > 0 ? (event.clientX - rect.left) / rect.width : 0,
      y: rect.height > 0 ? (event.clientY - rect.top) / rect.height : 0
    };
  };

  const panMode = tool === "pan" || spaceHeld;

  const handlePointerDown = (event: React.PointerEvent<HTMLCanvasElement>): void => {
    if (!isReady || isExporting) {
      return;
    }
    // Средняя кнопка / режим «Рука» / зажатый пробел — тащим картинку.
    if (event.button === 1 || (event.button === 0 && panMode)) {
      const viewport = viewportRef.current;
      if (!viewport) {
        return;
      }
      event.preventDefault();
      event.currentTarget.setPointerCapture(event.pointerId);
      panRef.current = {
        pointerId: event.pointerId,
        startX: event.clientX,
        startY: event.clientY,
        scrollLeft: viewport.scrollLeft,
        scrollTop: viewport.scrollTop
      };
      setIsPanning(true);
      return;
    }
    if (event.button !== 0) {
      return;
    }
    const point = pointFromEvent(event);
    event.currentTarget.setPointerCapture(event.pointerId);
    draftRef.current = {
      fromX: point.x,
      fromY: point.y,
      toX: point.x,
      toY: point.y,
      color,
      widthRatio
    };
  };

  const handlePointerMove = (event: React.PointerEvent<HTMLCanvasElement>): void => {
    const pan = panRef.current;
    if (pan && pan.pointerId === event.pointerId) {
      const viewport = viewportRef.current;
      if (viewport) {
        viewport.scrollLeft = pan.scrollLeft - (event.clientX - pan.startX);
        viewport.scrollTop = pan.scrollTop - (event.clientY - pan.startY);
      }
      return;
    }
    if (!draftRef.current) {
      return;
    }
    const point = pointFromEvent(event);
    draftRef.current = { ...draftRef.current, toX: point.x, toY: point.y };
    redraw();
  };

  const handlePointerUp = (event: React.PointerEvent<HTMLCanvasElement>): void => {
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    const pan = panRef.current;
    if (pan && pan.pointerId === event.pointerId) {
      panRef.current = null;
      setIsPanning(false);
      return;
    }
    const draft = draftRef.current;
    draftRef.current = null;
    if (!draft) {
      return;
    }
    const length = Math.hypot(draft.toX - draft.fromX, draft.toY - draft.fromY);
    if (length < MIN_ARROW_RATIO) {
      redraw();
      return;
    }
    setArrows((previous) => [...previous, draft]);
  };

  const apply = (): void => {
    const canvas = canvasRef.current;
    if (!canvas || arrows.length === 0) {
      return;
    }
    setIsExporting(true);
    // PNG сохраняем PNG (скриншоты обычно приходят так и не «мылятся»),
    // всё остальное отдаём JPEG — иначе фото на 3 МБ превращается в 12 МБ.
    const keepPng = file.type === "image/png";
    const mimeType = keepPng ? "image/png" : "image/jpeg";
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          setIsExporting(false);
          return;
        }
        const baseName = file.name.replace(/\.[^.]+$/, "") || "photo";
        const nextName = `${baseName}.${keepPng ? "png" : "jpg"}`;
        onApply(new File([blob], nextName, { type: mimeType }));
      },
      mimeType,
      0.92
    );
  };

  // Пока «вписанный» размер не посчитан — холст прячем, иначе на первый
  // кадр в сцену влетает картинка в натуральную величину.
  const canvasStyle: React.CSSProperties = fitSize
    ? {
        width: `${Math.round(fitSize.width * zoom)}px`,
        height: `${Math.round(fitSize.height * zoom)}px`
      }
    : { visibility: "hidden", maxWidth: "100%", maxHeight: "100%" };
  const canvasClassName = [
    "photo-annotator__canvas",
    panMode || isPanning ? "photo-annotator__canvas--pan" : "",
    isPanning ? "photo-annotator__canvas--panning" : ""
  ]
    .filter(Boolean)
    .join(" ");
  const zoomPercent = Math.round(zoom * 100);

  return (
    <div
      className="photo-annotator"
      role="dialog"
      aria-modal="true"
      aria-label="Стрелки на фото"
    >
      <button
        type="button"
        className="photo-annotator__backdrop"
        onClick={onCancel}
        aria-label="Закрыть"
      />
      <div className="photo-annotator__panel">
        <div className="photo-annotator__header">
          <strong>Стрелки на фото</strong>
          <button type="button" className="icon-button" onClick={onCancel} aria-label="Закрыть">
            ×
          </button>
        </div>
        <div className="photo-annotator__toolbar">
          <div className="photo-annotator__group" role="group" aria-label="Инструмент">
            <button
              type="button"
              className={
                tool === "arrow"
                  ? "button button--ghost photo-annotator__thickness--active"
                  : "button button--ghost"
              }
              onClick={() => setTool("arrow")}
              aria-pressed={tool === "arrow"}
              title="Рисовать стрелки"
            >
              Стрелка
            </button>
            <button
              type="button"
              className={
                tool === "pan"
                  ? "button button--ghost photo-annotator__thickness--active"
                  : "button button--ghost"
              }
              onClick={() => setTool("pan")}
              aria-pressed={tool === "pan"}
              title="Перетаскивать увеличенное фото (или зажмите пробел / среднюю кнопку мыши)"
            >
              Рука
            </button>
          </div>
          <div className="photo-annotator__group" role="group" aria-label="Цвет стрелки">
            {COLORS.map((option) => (
              <button
                key={option.value}
                type="button"
                className={
                  option.value === color
                    ? "photo-annotator__color photo-annotator__color--active"
                    : "photo-annotator__color"
                }
                style={{ background: option.value }}
                onClick={() => setColor(option.value)}
                title={option.label}
                aria-label={option.label}
                aria-pressed={option.value === color}
              />
            ))}
          </div>
          <div className="photo-annotator__group" role="group" aria-label="Толщина стрелки">
            {THICKNESS.map((option) => (
              <button
                key={option.value}
                type="button"
                className={
                  option.value === widthRatio
                    ? "button button--ghost photo-annotator__thickness--active"
                    : "button button--ghost"
                }
                onClick={() => setWidthRatio(option.value)}
                aria-pressed={option.value === widthRatio}
              >
                {option.label}
              </button>
            ))}
          </div>
          <div className="photo-annotator__group">
            <button
              type="button"
              className="button button--ghost"
              onClick={() => setArrows((previous) => previous.slice(0, -1))}
              disabled={arrows.length === 0}
            >
              Отменить стрелку
            </button>
            <button
              type="button"
              className="button button--ghost"
              onClick={() => setArrows([])}
              disabled={arrows.length === 0}
            >
              Очистить
            </button>
          </div>
          <div className="photo-annotator__group photo-annotator__zoom" role="group" aria-label="Масштаб">
            <button
              type="button"
              className="button button--ghost photo-annotator__zoom-button"
              onClick={() => applyZoom(zoom / ZOOM_STEP)}
              disabled={!isReady || zoom <= ZOOM_MIN + 0.001}
              title="Отдалить (−)"
              aria-label="Отдалить"
            >
              −
            </button>
            <button
              type="button"
              className="button button--ghost photo-annotator__zoom-value"
              onClick={() => applyZoom(1)}
              disabled={!isReady || zoom <= ZOOM_MIN + 0.001}
              title="Вписать в окно (0)"
            >
              {zoomPercent}%
            </button>
            <button
              type="button"
              className="button button--ghost photo-annotator__zoom-button"
              onClick={() => applyZoom(zoom * ZOOM_STEP)}
              disabled={!isReady || zoom >= ZOOM_MAX - 0.001}
              title="Приблизить (+)"
              aria-label="Приблизить"
            >
              +
            </button>
          </div>
        </div>
        <div className="photo-annotator__stage">
          {loadFailed ? (
            <div className="photo-annotator__error">
              Не удалось открыть изображение. Отправьте файл как есть.
            </div>
          ) : (
            <div className="photo-annotator__viewport" ref={viewportRef}>
              <canvas
                ref={canvasRef}
                className={canvasClassName}
                style={canvasStyle}
                onPointerDown={handlePointerDown}
                onPointerMove={handlePointerMove}
                onPointerUp={handlePointerUp}
                onPointerCancel={handlePointerUp}
                onContextMenu={(event) => event.preventDefault()}
              />
            </div>
          )}
        </div>
        <div className="photo-annotator__hint">
          Тяните мышью от начала стрелки к тому месту, куда нужно указать.
          Стрелок можно поставить сколько угодно. Приблизить: Ctrl+колесо или
          «+»/«−»; передвинуть увеличенное фото — колесом, инструментом «Рука»
          или с зажатым пробелом.
        </div>
        <div className="photo-annotator__actions">
          <button type="button" className="button button--ghost" onClick={onCancel}>
            Отмена
          </button>
          <button
            type="button"
            className="button button--primary"
            onClick={apply}
            disabled={arrows.length === 0 || isExporting || !isReady}
          >
            {isExporting ? "Сохраняем…" : "Готово"}
          </button>
        </div>
      </div>
    </div>
  );
};
